/**
 * Pure helpers for `TextStatsTool`. Tests live in
 * `tests/web/textStatsTool.test.ts`.
 *
 * All counters are codepoint-aware (`Array.from(text)`) so a single
 * emoji or surrogate pair counts as 1 character, matching what users
 * see in the UI. UTF-8 byte count is the on-the-wire form for
 * server-side limit checks.
 */

export interface TextStats {
  /** Codepoint count (emoji + surrogate-pair aware). */
  chars: number;
  /** Codepoint count after stripping every Unicode whitespace char. */
  charsNoSpace: number;
  /** Whitespace-delimited "words" (lowest-common-denominator definition; CJK is *not* segmented). */
  words: number;
  /** Number of LF-terminated lines, counting the final line even when unterminated. */
  lines: number;
  /** Paragraphs separated by one or more blank lines. */
  paragraphs: number;
  /** Sentences ending with `. ! ? 。 ！ ？` (English + Chinese full stops). */
  sentences: number;
  /** UTF-8 byte length, computed once via `TextEncoder`. */
  utf8Bytes: number;
  /** Average chars / line; 0 when there are no lines. */
  avgLineLength: number;
  /** Longest line by codepoint count, with its 1-based line number. */
  longestLine: { length: number; lineNo: number };
  /** Reading time in minutes (`words / 200`, rounded up to whole minutes). */
  readingMinutes: number;
}

const READING_WORDS_PER_MINUTE = 200;
const SENTENCE_TERMINATORS = /[.!?。！？]+/g;
// `\p{White_Space}` is a Unicode property escape — well-supported.
const WHITESPACE_RE = /\p{White_Space}/gu;
const WORD_RE = /\S+/g;
const PARAGRAPH_RE = /\n[ \t]*\n/;

/**
 * Compute every stat in one pass where possible. Designed to handle a
 * 1 MB input synchronously in well under 50 ms (no regex backtracking
 * traps; no per-character allocations beyond `Array.from`).
 */
export function computeTextStats(text: string): TextStats {
  if (text.length === 0) {
    return {
      chars: 0,
      charsNoSpace: 0,
      words: 0,
      lines: 0,
      paragraphs: 0,
      sentences: 0,
      utf8Bytes: 0,
      avgLineLength: 0,
      longestLine: { length: 0, lineNo: 0 },
      readingMinutes: 0,
    };
  }

  // Codepoint iteration — `Array.from(text)` gives one entry per
  // Unicode codepoint (so '🎉' is length 1 even though
  // `'🎉'.length === 2` for the UTF-16 surrogate pair).
  const codepoints = Array.from(text);
  const chars = codepoints.length;

  let charsNoSpace = 0;
  for (const cp of codepoints) {
    WHITESPACE_RE.lastIndex = 0;
    if (!WHITESPACE_RE.test(cp)) charsNoSpace++;
  }

  const wordMatches = text.match(WORD_RE);
  const words = wordMatches ? wordMatches.length : 0;

  // `text.split('\n')` is the cheapest line splitter and matches what
  // most editors show in the gutter.
  const lineArr = text.split('\n');
  const lines = lineArr.length;

  // Longest line by codepoint count; first occurrence wins on ties.
  let longestLength = 0;
  let longestLineNo = 0;
  let totalLineLengthCp = 0;
  for (let i = 0; i < lineArr.length; i++) {
    const len = countCodepoints(lineArr[i]);
    totalLineLengthCp += len;
    if (len > longestLength) {
      longestLength = len;
      longestLineNo = i + 1;
    }
  }
  const avgLineLength = lines > 0 ? Math.round(totalLineLengthCp / lines) : 0;

  // Paragraph: split on one-or-more blank line. Empty paragraphs at
  // start / end are filtered to match human intuition.
  const paragraphs = text
    .split(PARAGRAPH_RE)
    .map((p) => p.trim())
    .filter((p) => p.length > 0).length;

  const sentenceMatches = text.match(SENTENCE_TERMINATORS);
  const sentences = sentenceMatches ? sentenceMatches.length : 0;

  const utf8Bytes = new TextEncoder().encode(text).byteLength;

  const readingMinutes =
    words === 0 ? 0 : Math.max(1, Math.ceil(words / READING_WORDS_PER_MINUTE));

  return {
    chars,
    charsNoSpace,
    words,
    lines,
    paragraphs,
    sentences,
    utf8Bytes,
    avgLineLength,
    longestLine: { length: longestLength, lineNo: longestLineNo },
    readingMinutes,
  };
}

function countCodepoints(s: string): number {
  // Tight loop is faster than `Array.from(s).length` on huge strings
  // because it avoids materialising the array.
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff && i + 1 < s.length) {
      const next = s.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        i++; // skip low surrogate
      }
    }
    n++;
  }
  return n;
}
